// 50 MB: multi-thousand-line mineral statement bundles legitimately run tens
// of MB (matches the v2 API's UPLOAD_MAX_BYTES guard).
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const TERMINAL_STATUSES = new Set(["done", "failed", "rejected"]);

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 bytes";
  if (bytes < 1024) return `${bytes} bytes`;

  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const sizeReason = (file, maxBytes) =>
  `${file.name} is ${formatBytes(file.size)}. The upload limit is ${formatBytes(maxBytes)} per file.`;

const toDetail = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || fallback;
  if (typeof value.message === "string" && value.message) return value.message;
  return fallback;
};

const safeStatus = (onStatus, index, payload) => {
  if (typeof onStatus !== "function") return;
  try {
    onStatus(index, payload);
  } catch (_error) {
    // Status callbacks are observational and must not break the batch.
  }
};

export function validateFiles(files, maxBytes = MAX_UPLOAD_BYTES) {
  const accepted = [];
  const rejected = [];

  for (const file of files || []) {
    if (file.size <= maxBytes) {
      accepted.push(file);
    } else {
      rejected.push({ file, reason: sizeReason(file, maxBytes) });
    }
  }

  return { accepted, rejected };
}

export async function runIngestBatch({
  files,
  classifyOne,
  onStatus,
  maxBytes = MAX_UPLOAD_BYTES,
}) {
  const inputFiles = Array.from(files || []);
  const results = new Array(inputFiles.length);
  for (let index = 0; index < inputFiles.length; index += 1) {
    const file = inputFiles[index];
    if (file.size <= maxBytes) continue;

    const reason = sizeReason(file, maxBytes);
    const terminal = { file, name: file.name, status: "rejected", detail: reason };
    results[index] = { name: file.name, status: "rejected", detail: reason };
    safeStatus(onStatus, index, terminal);
  }

  for (let index = 0; index < inputFiles.length; index += 1) {
    if (results[index] && TERMINAL_STATUSES.has(results[index].status)) continue;

    const file = inputFiles[index];
    safeStatus(onStatus, index, {
      file,
      name: file.name,
      status: "reading",
      detail: "Reading document…",
    });

    try {
      const outcome = await classifyOne(file);
      const detail =
        outcome && typeof outcome === "object" && "detail" in outcome
          ? outcome.detail
          : undefined;
      const terminal = { file, name: file.name, status: "done", detail };
      results[index] = { name: file.name, status: "done", detail };
      safeStatus(onStatus, index, terminal);
    } catch (error) {
      const detail = toDetail(error, "Upload failed");
      const terminal = { file, name: file.name, status: "failed", detail };
      results[index] = { name: file.name, status: "failed", detail };
      safeStatus(onStatus, index, terminal);
    }
  }

  return results.map((result, index) => result || {
    name: inputFiles[index]?.name || `file-${index + 1}`,
    status: "failed",
    detail: "Upload failed",
  });
}
